import { Method } from 'axios';
import { Executable } from './executable';

/**
 * The Update Wallet Input used by the constructor of Update Wallet Notification.  Either personId or email are required to identify the wallet.
 */
export interface UpdateWalletNotificationInput {
  /**
   * Bambu person identifier
   */
  personId?: string;
  /**
   * Card Serial Number
   */
  serialNumber?: string;
  /**
   * Card Pass Identifier
   */
  passId?: number;
}

/**
 * The UpdateWalletNotification response expected from the execution of the object within BambuClient execute.
 */
export interface UpdateWalletNotificationResponse {
  /**
   * the message response from the request
   */
  message: string;
  /**
   * any error messages that were returned by the request.
   */
  error?: any;
  /**
   * If the person already exists, the personId is returned.
   */
  successful: boolean;
}

/**
 * The Update Wallet message takes a personId or email address and a message and uses that information to identify an existing wallet card within the tenant.  Then the message field on the card is udpated.
 */
export class UpdateWalletNotification extends Executable<UpdateWalletNotificationResponse> {
  /**
   * getMethod is used by the BambuClient to properly construct the request call.
   * @returns request method
   */
  public getMethod(): Method {
    return 'post';
  }

  /**
   * getResponse is used by the BambuClient to propery return the response object type.
   * @param apiResponse the api response object returned by the BambuMeta client before being converted to the UpdateWalletNotificationResponse
   * @returns UpdateWalletNotificationResponse
   */
  public getResponse(apiResponse: any): UpdateWalletNotificationResponse {
    return {
      successful: apiResponse && apiResponse.msg === 'Updated wallet',
      message: apiResponse.msg,
    };
  }

  /**
   * getURLPath is used by the BambuClient to properly construct the request call.
   * @param tenantId the tenant id defined in the BambuClient
   * @returns request url
   */
  public getURLPath(tenantId?: string): string {
    return `${tenantId}/wallet-notify`;
  }

  private _personId?: string;

  private _serialNo?: string;

  private _passId?: number;

  constructor(input?: UpdateWalletNotificationInput) {
    super();
    if (input) {
      this._serialNo = input.serialNumber;
      this._personId = input.personId;
      this._passId = input.passId;
    }
  }

  /**
   * Sets the personId on the person.
   * @param value <string> person id
   * @returns <UpdateWalletNotification>
   */
  public setPersonId(value: string): UpdateWalletNotification {
    this._personId = value;
    return this;
  }

  /**
   * Sets the serial number card.
   * @param value <string> email
   * @returns <UpdateWalletNotification>
   */
  public setSerialNo(value: string): UpdateWalletNotification {
    this._serialNo = value;
    return this;
  }

  /**
   * Sets the card passId
   * @param value <number> wallet card id.
   * @returns <UpdateWalletNotification>
   */
  public setPassId(value: number): UpdateWalletNotification {
    this._passId = value;
    return this;
  }

  /**
   * getBody is used by the BambuClient to properly construct the request object body.
   * @returns Returns the expected body of the request utilizing the defined request attributes.
   */
  getBody(): any {
    const input: any = {};
    if (this._passId) {
      input.passId = this._passId;
    }
    if (this._personId) {
      input.personId = this._personId;
    }
    if (this._serialNo) {
      input.serialNumber = this._serialNo;
    }
    return input;
  }

  /**
   * getRootPath is used by the BambuClient to properly construct the request call.
   * @returns The root path of the request
   */
  getRootPath(): string | undefined {
    return undefined;
  }
}
